import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaSearch, FaEye, FaFileInvoice } from "react-icons/fa";
import config from "../../config";

export const Invoice = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${config.API_BASE_URL}/customer/getCustomerOrders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = res.data?.data || [];
      setOrders(data);
      setFilteredOrders(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const formatDate = (date) => {
    if (!date) return "---";
    return new Date(date).toLocaleDateString("en-IN");
  };

  // ⭐ Search + Date Filter Logic
  useEffect(() => {
    let filtered = [...orders];

    const s = search.toLowerCase().trim();

    if (s !== "") {
      filtered = filtered.filter((order) => {
        const invoice = `INV-${order.order_number?.replace("ORD-", "")}`;
        const client = order.vendor_client || {};

        return (
          order.order_number?.toLowerCase().includes(s) ||
          invoice.toLowerCase().includes(s) ||
          client.phone?.toLowerCase().includes(s) ||
          client.email?.toLowerCase().includes(s)
        );
      });
    }

    // Date filters
    if (startDate) {
      const start = new Date(startDate + "T00:00:00");
      filtered = filtered.filter((order) => new Date(order.createdAt) >= start);
    }

    if (endDate) {
      const end = new Date(endDate + "T23:59:59");
      filtered = filtered.filter((order) => new Date(order.createdAt) <= end);
    }

    setFilteredOrders(filtered);
  }, [search, startDate, endDate, orders]);

  return (
    <div className="p-6">
      {/* Heading */}
      <h1 className="text-2xl font-bold text-gray-900 mb-5">Invoice List</h1>

      {/* Filters */}
      <div className="flex items-center flex-wrap gap-3 mb-5">
        {/* Search */}
        <div className="relative w-64">
          <FaSearch className="absolute top-3 left-3 text-gray-400" />
          <input
            type="text"
            placeholder="Search invoice, order, phone, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border px-10 py-2 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
          />
        </div>

        {/* Start Date */}
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="border px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
        />

        {/* End Date */}
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="border px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 outline-none"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto shadow-md rounded-lg scrollbar-hide">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className=" text-gray-700">
            <tr>
              <th className="py-3 px-4 text-left">INVOICE #</th>
              <th className="py-3 px-4 text-left">DATE</th>
              <th className="py-3 px-4 text-left">ORDER #</th>
              <th className="py-3 px-4 text-left">VENDOR DETAILS</th>
              <th className="py-3 px-4 text-left">AMOUNT</th>
              <th className="py-3 px-4 text-left">Paid</th>
              <th className="py-3 px-4 text-left">TAX</th>
              <th className="py-3 px-4 text-center">ACTION</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td className="py-5 text-center text-gray-500" colSpan="8">
                  Loading...
                </td>
              </tr>
            ) : filteredOrders.length === 0 ? (
              <tr>
                <td className="py-5 text-center text-gray-500" colSpan="8">
                  No Matching Records
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => {
                const vendor = order.vendor_info || {};

                return (
                  <tr
                    key={order._id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <td className="py-3 px-4 font-medium">
                      INV-{order.order_number?.replace("ORD-", "")}
                    </td>

                    <td className="py-3 px-4">{formatDate(order.createdAt)}</td>

                    <td className="py-3 px-4">{order.order_number}</td>

                    {/* ⭐ Updated Vendor Details */}
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="font-semibold">
                          {vendor.vendor_company_name || "---"}
                        </span>

                        <span className="text-gray-500 text-xs">
                          {vendor.vendor_name || "---"}
                        </span>

                        <span className="text-gray-500 text-xs">
                          {vendor.vendor_address?.officeAddress?.full_address ||
                            "---"}
                        </span>
                      </div>
                    </td>

                    <td className="py-3 px-4 font-semibold text-gray-800">
                      ₹{order.total_amount}
                    </td>

                    <td className="py-3 px-4">₹{order.total_discount}</td>

                    <td className="py-3 px-4">₹{order.total_gst}</td>

                    <td className="py-3 px-4 flex justify-center gap-3">
                      <button className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200">
                        <FaEye className="text-gray-600" />
                      </button>
                      <button className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200">
                        <FaFileInvoice className="text-gray-600" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
